// src/features/collaboration/services/collaborationService.js
import { 
  collection, 
  collectionGroup,
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  arrayUnion, 
  arrayRemove, 
  serverTimestamp,
  increment, 
  onSnapshot,
  writeBatch,
  runTransaction
} from "firebase/firestore";
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from 'uuid';
import { db, storage, functions } from "../../../firebase/config";

  
  export const fetchUserProfile = async (userId) => {
    try {
      if (!userId) {
        console.error("No user ID provided to fetchUserProfile");
        return null;
      }
      
      const userProfileDoc = await getDoc(doc(db, "userProfiles", userId));
      
      if (userProfileDoc.exists()) {
        return {
          id: userProfileDoc.id,
          ...userProfileDoc.data()
        };
      } else {
        console.log(`No user profile found for ID: ${userId}`);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      throw error;
    }
  };

  export const fetchPosts = async (options = {}) => {
    try {
      const { limitCount = 20, authorId } = options;
      
      let postsRef = collection(db, "posts");
      let q = query(postsRef, orderBy("createdAt", "desc"), limit(limitCount));
      
      if (authorId) {
        q = query(postsRef, where("authorId", "==", authorId), orderBy("createdAt", "desc"), limit(limitCount));
      }
      
      const querySnapshot = await getDocs(q);
      const posts = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const post = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Get author info from userProfiles collection
        const authorDoc = await getDoc(doc(db, "userProfiles", post.authorId));
        post.author = authorDoc.exists() 
          ? { id: authorDoc.id, ...authorDoc.data() }
          : { id: post.authorId, name: "Unknown User" };
        
        // Get comments count
        const commentsSnapshot = await getDocs(collection(db, "posts", post.id, "comments"));
        post.commentsCount = commentsSnapshot.size;
        
        posts.push(post);
      }
      
      return posts;
    } catch (error) {
      console.error("Error fetching posts:", error);
      throw error;
    }
  };
  
  // src/features/collaboration/services/collaborationService.js

  export const subscribeToPostFeed = (options = {}, callback) => {
    const { limitCount = 20, authorId } = options;
    
    let postsRef = collection(db, "posts");
    let q = query(postsRef, orderBy("createdAt", "desc"), limit(limitCount));
    
    if (authorId) {
      q = query(postsRef, where("authorId", "==", authorId), orderBy("createdAt", "desc"), limit(limitCount));
    }
    
    return onSnapshot(q, async (snapshot) => {
      const posts = [];
      
      // Process all documents in the snapshot
      for (const docSnapshot of snapshot.docs) {
        const post = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Fetch the author data from userProfiles collection for each post
        try {
          const authorDoc = await getDoc(doc(db, "userProfiles", post.authorId));
          if (authorDoc.exists()) {
            post.author = { id: authorDoc.id, ...authorDoc.data() };
          } else {
            post.author = { id: post.authorId, name: "Unknown User" };
          }
        } catch (error) {
          console.error(`Error fetching author for post ${post.id}:`, error);
          post.author = { id: post.authorId, name: "Unknown User" };
        }
        
        // Get comments
        try {
          const commentsSnapshot = await getDocs(collection(db, "posts", post.id, "comments"));
          post.comments = [];
          for (const commentDoc of commentsSnapshot.docs) {
            const comment = { id: commentDoc.id, ...commentDoc.data() };
            // Fetch user data from userProfiles collection for each comment
            const userDoc = await getDoc(doc(db, "userProfiles", comment.userId));
            if (userDoc.exists()) {
              comment.user = { id: userDoc.id, ...userDoc.data() };
            } else {
              comment.user = { id: comment.userId, name: "Unknown User" };
            }
            post.comments.push(comment);
          }
          post.commentsCount = post.comments.length;
        } catch (error) {
          console.error(`Error fetching comments for post ${post.id}:`, error);
          post.comments = [];
          post.commentsCount = 0;
        }
        
        posts.push(post);
      }
      
      // Sort posts by pin time then creation time (newest first)
      posts.sort((a, b) => {
        const aPinned = a.pinnedAt?.seconds ? a.pinnedAt.seconds : 0;
        const bPinned = b.pinnedAt?.seconds ? b.pinnedAt.seconds : 0;
        if (aPinned !== bPinned) return bPinned - aPinned;
        const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
        const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
        return bTime - aTime;
      });
      
      callback(posts);
    });
  };

  // Connections
  export const fetchConnections = async (userId) => {
    try {
      const pendingRef = collection(db, "connectionRequests");
      const pendingQuery = query(
        pendingRef, 
        where("toUserId", "==", userId),
        where("status", "==", "pending")
      );
      
      const connectionsRef = collection(db, "connections");
      const connectionsQuery = query(
        connectionsRef,
        where("users", "array-contains", userId)
      );
      
      const [pendingSnapshot, connectionsSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(connectionsQuery)
      ]);
      
      const connections = [];
      
      // Process pending requests
      for (const docSnapshot of pendingSnapshot.docs) {
        const request = { id: docSnapshot.id, ...docSnapshot.data() };
        // Get user info from userProfiles collection
        const userDoc = await getDoc(doc(db, "userProfiles", request.fromUserId));
        
        connections.push({
          id: request.id,
          user: userDoc.exists() 
            ? { id: userDoc.id, ...userDoc.data() }
            : { id: request.fromUserId, name: "Unknown User" },
          status: "pending",
          requestDate: request.createdAt.toDate()
        });
      }
      
      // Process active connections
      for (const docSnapshot of connectionsSnapshot.docs) {
        const connection = { id: docSnapshot.id, ...docSnapshot.data() };
        const otherUserId = connection.users.find(id => id !== userId);
        // Get user info from userProfiles collection
        const userDoc = await getDoc(doc(db, "userProfiles", otherUserId));
        
        connections.push({
          id: connection.id,
          user: userDoc.exists() 
            ? { id: userDoc.id, ...userDoc.data() }
            : { id: otherUserId, name: "Unknown User" },
          status: "active",
          connectedDate: connection.createdAt.toDate()
        });
      }
      
      return connections;
    } catch (error) {
      console.error("Error fetching connections:", error);
      throw error;
    }
  };
  
  // Messages
  export const fetchMessages = async (userId) => {
    try {
      const messagesRef = collectionGroup(db, "tm-messages");
      const q = query(
        messagesRef,
        where("recipientId", "==", userId),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const messages = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const message = { id: docSnapshot.id, ...docSnapshot.data() };
        // Get sender info from userProfiles collection
        const senderDoc = await getDoc(doc(db, "userProfiles", message.senderId));
        
        messages.push({
          id: message.id,
          content: message.content,
          timestamp: message.createdAt.toDate(),
          read: message.read,
          sender: senderDoc.exists() ? {
            id: senderDoc.id,
            name: senderDoc.data().name,
            profileImage: senderDoc.data().profileImage,
            online: false // You would need a separate online status tracking
          } : {
            id: message.senderId,
            name: "Unknown User",
            profileImage: null,
            online: false
          }
        });
      }
      
      return messages;
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  };

  // Fetch messages where user is sender or recipient (for threads)
  export const fetchMessagesForUser = async (userId, limitCount = 200) => {
    try {
      const conversationsRef = collection(db, "tm-conversations");
      const conversationsQuery = query(
        conversationsRef,
        where("participants", "array-contains", userId)
      );
      const conversationsSnap = await getDocs(conversationsQuery);

      const profileCache = new Map();
      const getProfile = async (id) => {
        if (profileCache.has(id)) return profileCache.get(id);
        const userDoc = await getDoc(doc(db, "userProfiles", id));
        const data = userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : { id, name: "Unknown User" };
        profileCache.set(id, data);
        return data;
      };

      const readSnapshot = async (snap) => {
        const list = [];
        for (const docSnapshot of snap.docs) {
          const message = { id: docSnapshot.id, ...docSnapshot.data() };
          const sender = await getProfile(message.senderId);
          const recipient = await getProfile(message.recipientId);
          list.push({
            id: message.id,
            content: message.content,
            createdAt: message.createdAt,
            read: message.read,
            sender,
            recipient,
            senderId: message.senderId,
            recipientId: message.recipientId
          });
        }
        return list;
      };

      const allMessages = [];
      for (const convoDoc of conversationsSnap.docs) {
        const convoId = convoDoc.id;
        const messagesRef = collection(db, "tm-conversations", convoId, "tm-messages");
        const messagesQuery = query(
          messagesRef,
          orderBy("createdAt", "desc"),
          limit(limitCount)
        );
        const messagesSnap = await getDocs(messagesQuery);
        const list = await readSnapshot(messagesSnap);
        allMessages.push(...list);
      }

      allMessages.sort((a, b) => {
        const aTime = a.createdAt?.seconds ? a.createdAt.seconds : new Date(a.createdAt || 0).getTime() / 1000;
        const bTime = b.createdAt?.seconds ? b.createdAt.seconds : new Date(b.createdAt || 0).getTime() / 1000;
        return bTime - aTime;
      });

      return allMessages;
    } catch (error) {
      console.error("Error fetching messages for user:", error);
      throw error;
    }
  };

  // Notifications
  const buildNotificationPayload = (payload = {}) => ({
    recipientId: payload.recipientId,
    actorId: payload.actorId || null,
    type: payload.type || 'activity',
    subtype: payload.subtype || null,
    title: payload.title || '',
    message: payload.message || '',
    entityId: payload.entityId || null,
    entityType: payload.entityType || null,
    link: payload.link || null,
    data: payload.data || {},
    read: false,
    createdAt: serverTimestamp()
  });

  export const createNotification = async (payload) => {
    if (!payload?.recipientId) return null;
    const notificationsRef = collection(db, "tm-notifications");
    const docRef = await addDoc(notificationsRef, buildNotificationPayload(payload));
    return docRef.id;
  };

  export const subscribeToNotifications = (userId, callback, limitCount = 50) => {
    if (!userId) return () => {};
    const notificationsRef = collection(db, "tm-notifications");
    const q = query(
      notificationsRef,
      where("recipientId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    return onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      callback(list);
    }, (error) => {
      console.error("subscribeToNotifications error", error);
      callback([]);
    });
  };

  export const subscribeToUnreadMessages = (userId, callback) => {
    if (!userId) return () => {};
    const messagesRef = collectionGroup(db, "tm-messages");
    const q = query(
      messagesRef,
      where("recipientId", "==", userId),
      where("read", "==", false)
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    }, (error) => {
      console.error("subscribeToUnreadMessages error", error);
      callback(0);
    });
  };

  export const markNotificationRead = async (notificationId) => {
    if (!notificationId) return false;
    const ref = doc(db, "tm-notifications", notificationId);
    await updateDoc(ref, { read: true, readAt: serverTimestamp() });
    return true;
  };

  export const markAllNotificationsRead = async (userId) => {
    if (!userId) return false;
    const notificationsRef = collection(db, "tm-notifications");
    const q = query(
      notificationsRef,
      where("recipientId", "==", userId),
      where("read", "==", false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return true;

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true, readAt: serverTimestamp() });
    });
    await batch.commit();
    return true;
  };

  export const markConversationRead = async (userId, otherUserId) => {
    if (!userId || !otherUserId) return false;
    const conversationId = [userId, otherUserId].sort().join("_");
    const messagesRef = collection(db, "tm-conversations", conversationId, "tm-messages");
    const q = query(
      messagesRef,
      where("recipientId", "==", userId),
      where("read", "==", false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return true;
    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true });
    });
    await batch.commit();
    return true;
  };
  
  export const sendMessage = async (senderId, recipientId, content) => {
    try {
      const participants = [senderId, recipientId].sort();
      const conversationId = participants.join("_");
      const conversationRef = doc(db, "tm-conversations", conversationId);
      await setDoc(
        conversationRef,
        {
          participants,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastMessage: {
            content,
            senderId,
            recipientId,
            createdAt: serverTimestamp(),
            read: false
          }
        },
        { merge: true }
      );

      const messageRef = await addDoc(collection(db, "tm-conversations", conversationId, "tm-messages"), {
        senderId,
        recipientId,
        content,
        participants,
        conversationId,
        createdAt: serverTimestamp(),
        read: false
      });

      await updateDoc(conversationRef, {
        updatedAt: serverTimestamp(),
        lastMessage: {
          content,
          senderId,
          recipientId,
          createdAt: serverTimestamp(),
          read: false
        }
      });
      
      return { id: messageRef.id, success: true };
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

export const acceptConnectionRequest = async (requestId) => {
    try {
      // Get the request data
      const requestRef = doc(db, "connectionRequests", requestId);
      const requestSnapshot = await getDoc(requestRef);
      
      if (!requestSnapshot.exists()) {
        throw new Error("Connection request not found");
      }
      
      const requestData = requestSnapshot.data();
      
      // Create a new connection in the connections collection
      const connectionRef = await addDoc(collection(db, "connections"), {
        users: [requestData.fromUserId, requestData.toUserId],
        createdAt: serverTimestamp()
      });
      
      // Update the request status
      await updateDoc(requestRef, {
        status: "accepted",
        updatedAt: serverTimestamp()
      });
      
      if (requestData.fromUserId && requestData.fromUserId !== requestData.toUserId) {
        await createNotification({
          recipientId: requestData.fromUserId,
          actorId: requestData.toUserId,
          type: 'connection',
          subtype: 'accepted',
          title: 'Connection accepted',
          message: 'Your connection request was accepted.',
          entityType: 'connection',
          entityId: connectionRef.id,
          link: '/connections'
        });
      }

      return { success: true };
    } catch (error) {
      console.error("Error accepting connection request:", error);
      throw error;
    }
  };
  
  // Ignore/decline a connection request
  export const ignoreConnectionRequest = async (requestId) => {
    try {
      const requestRef = doc(db, "connectionRequests", requestId);
      
      // Update the request status
      await updateDoc(requestRef, {
        status: "declined",
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error("Error ignoring connection request:", error);
      throw error;
    }
  };
  
  // Subscribe to connections (real-time updates)
  export const subscribeToConnections = (userId, callback) => {
  // Create a merged listener for both pending requests and active connections
  const pendingRef = collection(db, "connectionRequests");
  const pendingQuery = query(
    pendingRef, 
    where("toUserId", "==", userId),
    where("status", "==", "pending")
  );
  
  const connectionsRef = collection(db, "connections");
  const connectionsQuery = query(
    connectionsRef,
    where("users", "array-contains", userId)
  );
  
  // Set up the listeners
  const unsubscribePending = onSnapshot(pendingQuery, async (snapshot) => {
    const connections = [];
    
    for (const docChange of snapshot.docChanges()) {
      if (docChange.type === "added" || docChange.type === "modified") {
        const request = { id: docChange.doc.id, ...docChange.doc.data() };
        if (request.status === "pending") {
          // Get user info from userProfiles collection
          const userDoc = await getDoc(doc(db, "userProfiles", request.fromUserId));
          
          connections.push({
            id: request.id,
            user: userDoc.exists() 
              ? { id: userDoc.id, ...userDoc.data() }
              : { id: request.fromUserId, name: "Unknown User" },
            status: "pending",
            requestDate: request.createdAt.toDate()
          });
        }
      }
    }
    
    // Get active connections separately to avoid race conditions
    const activeConnectionsSnapshot = await getDocs(connectionsQuery);
    const activeConnections = [];
    
    for (const docSnapshot of activeConnectionsSnapshot.docs) {
      const connection = { id: docSnapshot.id, ...docSnapshot.data() };
      const otherUserId = connection.users.find(id => id !== userId);
      // Get user info from userProfiles collection
      const userDoc = await getDoc(doc(db, "userProfiles", otherUserId));
      
      activeConnections.push({
        id: connection.id,
        user: userDoc.exists() 
          ? { id: userDoc.id, ...userDoc.data() }
          : { id: otherUserId, name: "Unknown User" },
        status: "active",
        connectedDate: connection.createdAt.toDate()
      });
    }
    
    // Combine and call the callback
    callback([...connections, ...activeConnections]);
  });
  
  // Return unsubscribe function
  return () => {
    unsubscribePending();
  };
};
  
  // Like post
  export const likePost = async (postId, userId) => {
    try {
      const postRef = doc(db, "posts", postId);
      const postSnapshot = await getDoc(postRef);
      
      if (!postSnapshot.exists()) {
        throw new Error("Post not found");
      }
      
      const postData = postSnapshot.data();
      const isLiked = postData.likedBy?.includes(userId);
      
      if (!isLiked) {
        // Like the post
        await updateDoc(postRef, {
          likes: (postData.likes || 0) + 1,
          likedBy: arrayUnion(userId)
        });
        if (postData.authorId && postData.authorId !== userId) {
          await createNotification({
            recipientId: postData.authorId,
            actorId: userId,
            type: 'activity',
            subtype: 'post_like',
            title: 'New like',
            message: 'Someone liked your post.',
            entityType: 'post',
            entityId: postId,
            link: `/post/${postId}`
          });
        }
        return { liked: true };
      } else {
        // Unlike the post
        await updateDoc(postRef, {
          likes: Math.max(0, (postData.likes || 0) - 1),
          likedBy: arrayRemove(userId)
        });
        return { liked: false };
      }
    } catch (error) {
      console.error("Error liking post:", error);
      throw error;
    }
  };

  export const commentOnPost = async (postId, userId, commentText) => {
    try {
      const postRef = doc(db, "posts", postId);
      const postSnapshot = await getDoc(postRef);

      if (!postSnapshot.exists()) {
        throw new Error("Post not found");
      }

      const postData = postSnapshot.data();
      // Reference to the comments subcollection of the post
      const commentsRef = collection(db, "posts", postId, "comments");
      
      // Create the comment document
      const commentRef = await addDoc(commentsRef, {
        userId: userId,
        text: commentText,
        createdAt: serverTimestamp()
      });
      
      // Get the newly created comment
      const commentSnapshot = await getDoc(commentRef);
      const newComment = { id: commentSnapshot.id, ...commentSnapshot.data() };
      
      // Get user info from userProfiles collection for the comment
      const userDoc = await getDoc(doc(db, "userProfiles", userId));
      const userData = userDoc.exists() ? userDoc.data() : { name: "Unknown User" };

      if (postData.authorId && postData.authorId !== userId) {
        await createNotification({
          recipientId: postData.authorId,
          actorId: userId,
          type: 'activity',
          subtype: 'post_comment',
          title: 'New comment',
          message: 'Someone commented on your post.',
          entityType: 'post',
          entityId: postId,
          link: `/post/${postId}`
        });
      }
      
      // Return the complete comment with user data
      return {
        id: newComment.id,
        text: newComment.text,
        createdAt: newComment.createdAt,
        user: {
          id: userId,
          name: userData.name,
          profileImage: userData.profileImage
        }
      };
    } catch (error) {
      console.error("Error commenting on post:", error);
      throw error;
    }
  };
  
  // Fetch comments for a post
  export const fetchComments = async (postId) => {
    try {
      const commentsRef = collection(db, "posts", postId, "comments");
      const q = query(commentsRef, orderBy("createdAt", "asc"));
      
      const querySnapshot = await getDocs(q);
      const comments = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const comment = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Get user info from userProfiles collection
        const userDoc = await getDoc(doc(db, "userProfiles", comment.userId));
        const userData = userDoc.exists() ? userDoc.data() : { name: "Unknown User" };
        
        comments.push({
          id: comment.id,
          text: comment.text,
          createdAt: comment.createdAt,
          user: {
            id: comment.userId,
            name: userData.name,
            profileImage: userData.profileImage
          }
        });
      }
      
      return comments;
    } catch (error) {
      console.error("Error fetching comments:", error);
      throw error;
    }
  };
  
  // Subscribe to comments for real-time updates
  export const subscribeToComments = (postId, callback) => {
    const commentsRef = collection(db, "posts", postId, "comments");
    const q = query(commentsRef, orderBy("createdAt", "asc"));
    
    return onSnapshot(q, async (snapshot) => {
      const comments = [];
      
      for (const docChange of snapshot.docChanges()) {
        if (docChange.type === "added" || docChange.type === "modified") {
          const comment = { id: docChange.doc.id, ...docChange.doc.data() };
          
          // Get user info from userProfiles collection
          const userDoc = await getDoc(doc(db, "userProfiles", comment.userId));
          const userData = userDoc.exists() ? userDoc.data() : { name: "Unknown User" };
          
          comments.push({
            id: comment.id,
            text: comment.text,
            createdAt: comment.createdAt,
            user: {
              id: comment.userId,
              name: userData.name,
              profileImage: userData.profileImage
            }
          });
        }
      }
      
      // Sort by creation time
      comments.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return a.createdAt.seconds - b.createdAt.seconds;
      });
      
      callback(comments);
    });
  };

  export const fetchUserPosts = async (userId) => {
    try {
      let postsRef = collection(db, "posts");
      let q = query(
        postsRef, 
        where("authorId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      const posts = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const post = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Get author info from userProfiles collection
        const authorDoc = await getDoc(doc(db, "userProfiles", post.authorId));
        post.author = authorDoc.exists() 
          ? { id: authorDoc.id, ...authorDoc.data() }
          : { id: post.authorId, name: "Unknown User" };
        
        // Get comments count
        const commentsSnapshot = await getDocs(collection(db, "posts", post.id, "comments"));
        post.commentsCount = commentsSnapshot.size;
        
        posts.push(post);
      }
      
      return posts;
    } catch (error) {
      console.error("Error fetching user posts:", error);
      throw error;
    }
  };

  export const subscribeToUserPosts = (userId, options = {}, callback) => {
    const { limitCount = 10 } = options;
    
    const postsRef = collection(db, "posts");
    const q = query(
      postsRef,
      where("authorId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
    
    return onSnapshot(q, async (snapshot) => {
      const posts = [];
      
      // Process posts
      for (const docChange of snapshot.docChanges()) {
        if (docChange.type === "added" || docChange.type === "modified") {
          const post = { id: docChange.doc.id, ...docChange.doc.data() };
          
          // Get author info from userProfiles collection
          const authorDoc = await getDoc(doc(db, "userProfiles", post.authorId));
          post.author = authorDoc.exists() 
            ? { id: authorDoc.id, ...authorDoc.data() }
            : { id: post.authorId, name: "Unknown User" };
          
          // Get comments count
          const commentsSnapshot = await getDocs(collection(db, "posts", post.id, "comments"));
          post.commentsCount = commentsSnapshot.size;
          
          posts.push(post);
        }
      }
      
      // Sort by creation time (newest first)
      posts.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.seconds - a.createdAt.seconds;
      });
      
      callback(posts);
    });
  };

  const MAX_OG_IMAGE_BYTES = 300 * 1024;

  const loadImageFromFile = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to read image file"));
    };
    img.src = objectUrl;
  });

  const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to compress image"));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

  const buildJpegName = (fileName = "image") => {
    const safeName = fileName.replace(/\.[^/.]+$/, "");
    return `${safeName}.jpg`;
  };

  const compressPostImageForOg = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return file;
    if (file.size <= MAX_OG_IMAGE_BYTES) return file;

    const img = await loadImageFromFile(file);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image processing is not supported in this browser");

    let width = img.width;
    let height = img.height;
    const maxDimension = 1600;
    if (width > maxDimension || height > maxDimension) {
      const scale = Math.min(maxDimension / width, maxDimension / height);
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));
    }

    const minDimension = 320;
    while (width >= minDimension && height >= minDimension) {
      canvas.width = width;
      canvas.height = height;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      for (let quality = 0.86; quality >= 0.4; quality -= 0.08) {
        const blob = await canvasToBlob(canvas, "image/jpeg", Number(quality.toFixed(2)));
        if (blob.size <= MAX_OG_IMAGE_BYTES) {
          return new File(
            [blob],
            buildJpegName(file.name),
            { type: "image/jpeg", lastModified: Date.now() },
          );
        }
      }

      width = Math.floor(width * 0.85);
      height = Math.floor(height * 0.85);
    }

    throw new Error("Image is too large. Please choose a smaller image.");
  };

  export const uploadMediaFile = async (userId, file, mediaType) => {
    if (!file) return { url: '', path: '' };
    const uploadFile = mediaType === 'image' ? await compressPostImageForOg(file) : file;
    
    const storage = getStorage();
    const fileExtension = uploadFile.name.split('.').pop();
    const fileName = `${mediaType}_${uuidv4()}.${fileExtension}`;
    const filePath = `post-images/${userId}/${fileName}`;
    const storageRef = ref(storage, filePath);
    
    await uploadBytes(storageRef, uploadFile);
    const downloadUrl = await getDownloadURL(storageRef);
    
    return {
      url: downloadUrl,
      path: filePath
    };
  };

  const normalizeDocumentsInput = (postData = {}, existingPost = null) => {
    if (Array.isArray(postData.documents)) {
      return postData.documents.slice(0, 5);
    }

    if (postData.mediaType === 'document') {
      if (postData.mediaFile) {
        return [{ file: postData.mediaFile, name: postData.mediaName || postData.mediaFile.name }];
      }

      if (postData.mediaUrl) {
        return [{ url: postData.mediaUrl, name: postData.mediaName || 'Document.pdf' }];
      }
    }

    if (existingPost) {
      if (Array.isArray(existingPost.documents)) {
        return existingPost.documents.slice(0, 5);
      }

      const legacyDocumentUrl = existingPost.documentFile || existingPost.documentUrl || '';
      if (legacyDocumentUrl) {
        return [{
          url: legacyDocumentUrl,
          name: existingPost.documentName || 'Document.pdf',
          path: existingPost.imagePath || ''
        }];
      }
    }

    return [];
  };

  const uploadPostDocuments = async (userId, documents = []) => {
    const documentEntries = documents.slice(0, 5);

    return Promise.all(documentEntries.map(async (document) => {
      if (document?.file) {
        const uploadResult = await uploadMediaFile(userId, document.file, 'document');
        return {
          name: document.name || document.file.name || 'Document.pdf',
          url: uploadResult.url,
          path: uploadResult.path
        };
      }

      if (document?.url) {
        return {
          name: document.name || 'Document.pdf',
          url: document.url,
          path: document.path || ''
        };
      }

      return null;
    })).then((uploadedDocuments) => uploadedDocuments.filter(Boolean));
  };

  const getLegacyDocumentFields = (documents = []) => {
    const [firstDocument] = documents;
    if (!firstDocument) {
      return {
        documentFile: null,
        documentUrl: null,
        documentName: null
      };
    }

    return {
      documentFile: firstDocument.path ? firstDocument.url : null,
      documentUrl: firstDocument.path ? null : firstDocument.url,
      documentName: firstDocument.name || 'Document.pdf'
    };
  };

  const URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;
  const MAX_LINK_PREVIEWS = 3;

  const extractUrlsFromText = (text = '') => {
    if (!text || typeof text !== 'string') {
      return [];
    }

    return Array.from(new Set((text.match(URL_PATTERN) || []).map((url) => url.replace(/[),.!?]+$/, ''))));
  };

  const getUrlsForPreview = (postData = {}) => {
    const urls = [
      ...extractUrlsFromText(postData.content),
      ...extractUrlsFromText(postData.article?.subtitle),
      ...extractUrlsFromText(postData.article?.body)
    ];

    const excludedUrls = new Set([
      postData.mediaUrl,
      postData.videoUrl,
      postData.documentUrl,
      ...(Array.isArray(postData.documents) ? postData.documents.map((document) => document?.url) : [])
    ].filter(Boolean));

    return urls.filter((url) => !excludedUrls.has(url)).slice(0, MAX_LINK_PREVIEWS);
  };

  const fetchLinkPreview = async (url) => {
    const callable = httpsCallable(functions, "fetchLinkPreview");
    const result = await callable({ url });
    return result?.data || null;
  };

  const resolveLinkPreviewFields = async (postData = {}) => {
    const urls = getUrlsForPreview(postData);
    if (urls.length === 0) {
      return {
        linkPreview: null,
        linkPreviews: []
      };
    }

    const previews = (await Promise.all(urls.map(async (url) => {
      try {
        const preview = await fetchLinkPreview(url);
        if (!preview?.url) {
          return null;
        }
        return preview;
      } catch (error) {
        console.warn("Failed to fetch link preview:", {url, error: error?.message || error});
        return {
          url,
          title: '',
          description: '',
          image: '',
          siteName: '',
          favicon: '',
          hostname: ''
        };
      }
    }))).filter(Boolean);

    return {
      linkPreview: previews[0] || null,
      linkPreviews: previews
    };
  };
  

export const createPost = async (userId, postData) => {
    if (!userId) {
      throw new Error("User ID is required to create a post");
    }
    
    try {
      let mediaUrl = '';
      let mediaPath = '';
      let mediaData = {};
      const documents = await uploadPostDocuments(userId, normalizeDocumentsInput(postData));
      const primaryMediaType = postData.mediaType || '';
      
      // Process media based on type
        if (postData.mediaType) {
          if (postData.mediaType === 'image' && postData.mediaFile) {
            const uploadResult = await uploadMediaFile(userId, postData.mediaFile, 'image');
            mediaUrl = uploadResult.url;
            mediaPath = uploadResult.path;
          } else if (postData.mediaType === 'image' && postData.mediaUrl) {
            mediaUrl = postData.mediaUrl;
          } else if (postData.mediaType === 'video') {
          if (postData.mediaFile) {
            const uploadResult = await uploadMediaFile(userId, postData.mediaFile, 'video');
            mediaUrl = uploadResult.url;
            mediaPath = uploadResult.path;
            mediaData.videoFile = uploadResult.url;
          } else if (postData.mediaUrl) {
            mediaUrl = postData.mediaUrl;
            mediaData.videoUrl = postData.mediaUrl;
            mediaData.videoUrlType = postData.mediaUrlType;
          }
        }
      }

      mediaData = {
        ...mediaData,
        documents,
        ...getLegacyDocumentFields(documents)
      };
      const linkPreviewFields = await resolveLinkPreviewFields(postData);
      
      // Create post document
      const postRef = await addDoc(collection(db, "posts"), {
        authorId: userId,
        content: postData.content,
        image: primaryMediaType === 'image' ? mediaUrl : '',
        imagePath: primaryMediaType === 'image' ? mediaPath : '',
        isPublic: postData.isPublic ?? true,
        createdAt: serverTimestamp(),
        likes: 0,
        likedBy: [],
        mediaType: primaryMediaType,
        postType: postData.postType || 'post',
        article: postData.article || null,
          subjects: postData.subjects || [],
          grades: postData.grades || [],
          category: postData.category || '',
          poll: postData.poll || null,
          ...linkPreviewFields,
          ...mediaData // Spread in any media-specific data
        });
      
      // Get the created post
      const postSnapshot = await getDoc(postRef);
      const post = { id: postSnapshot.id, ...postSnapshot.data() };
      
      // Get author info
      const authorDoc = await getDoc(doc(db, "userProfiles", userId));
      post.author = { id: authorDoc.id, ...authorDoc.data() };
      
      return post;
    } catch (error) {
      console.error("Error creating post:", error);
      throw error;
    }
  };

export const updatePost = async (postId, postData) => {
    if (!postId) {
      throw new Error("Post ID is required to update a post");
    }

    try {
      const postRef = doc(db, "posts", postId);
      const existingPostSnapshot = await getDoc(postRef);
      if (!existingPostSnapshot.exists()) {
        throw new Error("Post not found");
      }

      const existingPost = existingPostSnapshot.data();
      const authorId = existingPost.authorId || postData.authorId;
      let mediaUrl = '';
      let mediaPath = '';
      let mediaData = {};
      const documents = authorId
        ? await uploadPostDocuments(authorId, normalizeDocumentsInput(postData, existingPost))
        : normalizeDocumentsInput(postData, existingPost);
      const primaryMediaType = postData.mediaType || '';
      const preserveExistingImage = !postData.mediaType && existingPost.image && existingPost.mediaType !== 'video';
      const preserveExistingImagePath = !postData.mediaType && existingPost.imagePath && existingPost.mediaType !== 'video';
      const preserveExistingVideoFile = !postData.mediaType ? (existingPost.videoFile || null) : null;
      const preserveExistingVideoUrl = !postData.mediaType ? (existingPost.videoUrl || null) : null;
      const preserveExistingVideoUrlType = !postData.mediaType ? (existingPost.videoUrlType || null) : null;

      if (postData.mediaType) {
        if (!authorId) {
          throw new Error("Author ID is required to update post media");
        }

        if (postData.mediaType === 'image') {
          if (postData.mediaFile) {
            const uploadResult = await uploadMediaFile(authorId, postData.mediaFile, 'image');
            mediaUrl = uploadResult.url;
            mediaPath = uploadResult.path;
          } else if (postData.mediaUrl) {
            mediaUrl = postData.mediaUrl;
            mediaPath = existingPost.image === mediaUrl ? (existingPost.imagePath || '') : '';
          }
        } else if (postData.mediaType === 'video') {
          if (postData.mediaFile) {
            const uploadResult = await uploadMediaFile(authorId, postData.mediaFile, 'video');
            mediaUrl = uploadResult.url;
            mediaPath = uploadResult.path;
            mediaData.videoFile = uploadResult.url;
          } else if (postData.mediaUrl) {
            mediaUrl = postData.mediaUrl;
            mediaData.videoUrl = postData.mediaUrl;
            mediaData.videoUrlType = postData.mediaUrlType || existingPost.videoUrlType || '';
          }
        }
      }

      mediaData = {
        ...mediaData,
        documents,
        ...getLegacyDocumentFields(documents)
      };
      const linkPreviewFields = await resolveLinkPreviewFields(postData);

      await updateDoc(postRef, {
        content: postData.content,
        isPublic: postData.isPublic ?? true,
        postType: postData.postType || 'post',
        article: postData.article || null,
        subjects: postData.subjects || [],
        grades: postData.grades || [],
        category: postData.category || '',
        poll: postData.poll || null,
        image: postData.mediaType === 'image' ? mediaUrl : (preserveExistingImage ? existingPost.image : ''),
        imagePath: postData.mediaType === 'image' ? mediaPath : (preserveExistingImagePath ? existingPost.imagePath : ''),
        mediaType: primaryMediaType,
        videoFile: postData.mediaType === 'video' ? (mediaData.videoFile || null) : preserveExistingVideoFile,
        videoUrl: postData.mediaType === 'video' ? (mediaData.videoUrl || null) : preserveExistingVideoUrl,
        videoUrlType: postData.mediaType === 'video' ? (mediaData.videoUrlType || null) : preserveExistingVideoUrlType,
        documentFile: mediaData.documentFile,
        documentUrl: mediaData.documentUrl,
        documentName: mediaData.documentName,
        documents: mediaData.documents,
        linkPreview: linkPreviewFields.linkPreview,
        linkPreviews: linkPreviewFields.linkPreviews,
        updatedAt: serverTimestamp()
      });

      const postSnapshot = await getDoc(postRef);
      return { id: postSnapshot.id, ...postSnapshot.data() };
    } catch (error) {
      console.error("Error updating post:", error);
      throw error;
    }
  };

  export const voteOnPoll = async (postId, userId, selectedOptionIds) => {
    if (!postId || !userId || !selectedOptionIds?.length) {
      throw new Error("Post ID, user ID, and at least one option are required to vote");
    }

    try {
      const postRef = doc(db, "posts", postId);
      const voterRef = doc(db, "posts", postId, "pollVoters", userId);
      const uniqueOptionIds = [...new Set(selectedOptionIds)];

      const result = await runTransaction(db, async (tx) => {
        const [postSnap, voterSnap] = await Promise.all([
          tx.get(postRef),
          tx.get(voterRef),
        ]);

        if (!postSnap.exists()) {
          throw new Error("Post not found");
        }

        if (voterSnap.exists()) {
          throw new Error("You have already voted on this poll");
        }

        const postData = postSnap.data();
        if (!postData.poll) {
          throw new Error("This post does not have a poll");
        }

        if (!postData.poll.allowMultiple && uniqueOptionIds.length > 1) {
          throw new Error("This poll only allows a single selection");
        }

        const validOptionIds = new Set((postData.poll.options || []).map((opt) => opt.id));
        const invalidOption = uniqueOptionIds.find((optId) => !validOptionIds.has(optId));
        if (invalidOption) {
          throw new Error("Invalid poll option selected");
        }

        const updates = {
          'poll.totalVotes': increment(uniqueOptionIds.length),
        };

        uniqueOptionIds.forEach((optId) => {
          updates[`poll.votes.${optId}`] = increment(1);
        });

        tx.update(postRef, updates);
        tx.set(voterRef, {
          userId,
          selectedOptionIds: uniqueOptionIds,
          createdAt: serverTimestamp(),
        });

        return {
          id: postSnap.id,
          ...postData,
          poll: {
            ...postData.poll,
            totalVotes: (postData.poll.totalVotes || 0) + uniqueOptionIds.length,
            votes: uniqueOptionIds.reduce((acc, optId) => ({
              ...acc,
              [optId]: (postData.poll.votes?.[optId] || 0) + 1,
            }), {...(postData.poll.votes || {})}),
          },
          userVoted: true,
        };
      });

      return result;
    } catch (error) {
      console.error("Error voting on poll:", error);
      throw error;
    }
  };

  export const deletePost = async (postId) => {
    if (!postId) {
      throw new Error("Post ID is required to delete a post");
    }
    try {
      await deleteDoc(doc(db, "posts", postId));
      return { success: true };
    } catch (error) {
      console.error("Error deleting post:", error);
      throw error;
    }
  };

  export const savePost = async (userId, postId) => {
    if (!userId || !postId) {
      throw new Error("User ID and post ID are required to save a post");
    }
    try {
      const saveId = `${userId}_${postId}`;
      await setDoc(doc(db, "tm-saved-posts", saveId), {
        userId,
        postId,
        createdAt: serverTimestamp()
      }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error("Error saving post:", error);
      throw error;
    }
  };

  export const reportPost = async (userId, postId, reason = '') => {
    if (!userId || !postId) {
      throw new Error("User ID and post ID are required to report a post");
    }
    try {
      await addDoc(collection(db, "tm-post-reports"), {
        reporterId: userId,
        postId,
        reason: reason?.trim() || 'unspecified',
        createdAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error("Error reporting post:", error);
      throw error;
    }
  };

  export const pinPost = async (postId, userId) => {
    if (!postId || !userId) {
      throw new Error("Post ID and user ID are required to pin a post");
    }
    try {
      const postRef = doc(db, "posts", postId);
      const postSnap = await getDoc(postRef);
      if (!postSnap.exists()) {
        throw new Error("Post not found");
      }
      const postData = postSnap.data();
      if (postData.authorId !== userId) {
        throw new Error("Only the author can pin this post");
      }

      const pinnedQuery = query(
        collection(db, "posts"),
        where("pinnedAt", "!=", null),
        orderBy("pinnedAt", "desc"),
        limit(2)
      );
      const pinnedSnap = await getDocs(pinnedQuery);
      const alreadyPinned = Boolean(postData.pinnedAt);

      if (!alreadyPinned && pinnedSnap.size >= 2) {
        throw new Error("Pin limit reached");
      }

      await updateDoc(postRef, {
        pinnedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error("Error pinning post:", error);
      throw error;
    }
  };

  export const unpinPost = async (postId, userId) => {
    if (!postId || !userId) {
      throw new Error("Post ID and user ID are required to unpin a post");
    }
    try {
      const postRef = doc(db, "posts", postId);
      const postSnap = await getDoc(postRef);
      if (!postSnap.exists()) {
        throw new Error("Post not found");
      }
      const postData = postSnap.data();
      if (postData.authorId !== userId) {
        throw new Error("Only the author can unpin this post");
      }
      await updateDoc(postRef, {
        pinnedAt: null
      });
      return { success: true };
    } catch (error) {
      console.error("Error unpinning post:", error);
      throw error;
    }
  };

  // Cancel a sent connection request
  export const cancelConnectionRequest = async (requestId) => {
    try {
      await deleteDoc(doc(db, "connectionRequests", requestId));
      return { success: true };
    } catch (error) {
      console.error("Error canceling connection request:", error);
      throw error;
    }
  };

  // Fetch pending requests for a user (incoming + outgoing)
  export const fetchPendingRequests = async (userId) => {
    try {
      const requestsRef = collection(db, "connectionRequests");
      const incomingQuery = query(
        requestsRef,
        where("toUserId", "==", userId),
        where("status", "==", "pending")
      );
      const outgoingQuery = query(
        requestsRef,
        where("fromUserId", "==", userId),
        where("status", "==", "pending")
      );

      const [incomingSnapshot, outgoingSnapshot] = await Promise.all([
        getDocs(incomingQuery),
        getDocs(outgoingQuery)
      ]);

      const incoming = [];
      for (const docSnapshot of incomingSnapshot.docs) {
        const request = { id: docSnapshot.id, ...docSnapshot.data() };
        const userDoc = await getDoc(doc(db, "userProfiles", request.fromUserId));
        incoming.push({
          ...request,
          user: userDoc.exists()
            ? { id: userDoc.id, ...userDoc.data() }
            : { id: request.fromUserId, name: "Unknown User" }
        });
      }

      const outgoing = [];
      for (const docSnapshot of outgoingSnapshot.docs) {
        const request = { id: docSnapshot.id, ...docSnapshot.data() };
        const userDoc = await getDoc(doc(db, "userProfiles", request.toUserId));
        outgoing.push({
          ...request,
          user: userDoc.exists()
            ? { id: userDoc.id, ...userDoc.data() }
            : { id: request.toUserId, name: "Unknown User" }
        });
      }

      return { incoming, outgoing };
    } catch (error) {
      console.error("Error fetching pending requests:", error);
      throw error;
    }
  };

  // Fetch a list of user profiles for discovery
  export const fetchNetworkUsers = async (userId, limitCount = 50) => {
    try {
      const usersRef = collection(db, "userProfiles");
      const q = query(usersRef, limit(limitCount));
      const snapshot = await getDocs(q);

      const users = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      return users.filter((user) => user.id !== userId);
    } catch (error) {
      console.error("Error fetching network users:", error);
      throw error;
    }
  };

  // Send a connection request (with duplicate checks)
  export const sendConnectionRequest = async (fromUserId, toUserId) => {
    if (!fromUserId || !toUserId || fromUserId === toUserId) {
      throw new Error("Invalid connection request");
    }

    try {
      // Check existing connection
      const connectionsRef = collection(db, "connections");
      const connectionsQuery = query(
        connectionsRef,
        where("users", "array-contains", fromUserId)
      );
      const connectionsSnapshot = await getDocs(connectionsQuery);
      const alreadyConnected = connectionsSnapshot.docs.some((docSnap) => {
        const data = docSnap.data();
        return Array.isArray(data.users) && data.users.includes(toUserId);
      });
      if (alreadyConnected) {
        return { success: false, reason: "already_connected" };
      }

      // Check pending requests in both directions
      const requestsRef = collection(db, "connectionRequests");
      const outgoingQuery = query(
        requestsRef,
        where("fromUserId", "==", fromUserId),
        where("toUserId", "==", toUserId),
        where("status", "==", "pending")
      );
      const incomingQuery = query(
        requestsRef,
        where("fromUserId", "==", toUserId),
        where("toUserId", "==", fromUserId),
        where("status", "==", "pending")
      );
      const [outgoingSnap, incomingSnap] = await Promise.all([
        getDocs(outgoingQuery),
        getDocs(incomingQuery)
      ]);
      if (!outgoingSnap.empty || !incomingSnap.empty) {
        return { success: false, reason: "request_exists" };
      }

      const requestRef = await addDoc(requestsRef, {
        fromUserId,
        toUserId,
        status: "pending",
        createdAt: serverTimestamp()
      });

      await createNotification({
        recipientId: toUserId,
        actorId: fromUserId,
        type: 'connection',
        subtype: 'request',
        title: 'New connection request',
        message: 'You have a new connection request.',
        entityType: 'connectionRequest',
        entityId: requestRef.id,
        link: '/connections/requests'
      });

      return { success: true };
    } catch (error) {
      console.error("Error sending connection request:", error);
      throw error;
    }
  };

  export const trackPostShare = async (postId, userId, platform) => {
    try {
      // Reference to the post
      const postRef = doc(db, "posts", postId);
      const postSnapshot = await getDoc(postRef);
      
      if (!postSnapshot.exists()) {
        throw new Error("Post not found");
      }
      
      const postData = postSnapshot.data();

      // Update share count and record sharing activity using ISO string instead of serverTimestamp
      await updateDoc(postRef, {
        shareCount: increment(1),
        sharedBy: arrayUnion({
          userId,
          platform,
          timestamp: new Date().toISOString() // Use ISO string instead of serverTimestamp
        })
      });
      
      // Analytics is best-effort and may be locked down by rules in some environments.
      try {
        await addDoc(collection(db, "analytics", "posts", "shares"), {
          postId,
          userId,
          platform,
          timestamp: serverTimestamp() // Server timestamp is fine here
        });
      } catch (analyticsError) {
        console.warn("Unable to write post share analytics:", analyticsError);
      }

      if (postData.authorId && postData.authorId !== userId) {
        await createNotification({
          recipientId: postData.authorId,
          actorId: userId,
          type: 'activity',
          subtype: 'post_share',
          title: 'Post shared',
          message: 'Someone shared your post.',
          entityType: 'post',
          entityId: postId,
          link: `/post/${postId}`
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error tracking post share:", error);
      throw error;
    }
  };
  
  // Add this function to generate permalink for a post
  export const getPostPermalink = async (postId) => {
    try {
      // Reference to the post
      const postRef = doc(db, "posts", postId);
      const postSnapshot = await getDoc(postRef);
      
      if (!postSnapshot.exists()) {
        throw new Error("Post not found");
      }
      
      // Check if this post already has a permalink
      const postData = postSnapshot.data();
      
      if (postData.permalink) {
        return postData.permalink;
      }
      
      // Generate a permalink based on post ID
      const permalink = `${window.location.origin}/post/${postId}`;
      
      // Update the post with the permalink
      await updateDoc(postRef, {
        permalink
      });
      
      return permalink;
    } catch (error) {
      console.error("Error generating post permalink:", error);
      // Return a fallback URL
      return `${window.location.origin}/post/${postId}`;
    }
  };

  export const createEvent = async (userId, eventData, bannerFile = null) => {
  try {
    const eventsRef = collection(db, 'tm-events');

    let bannerUrl = null;
    if (bannerFile) {
      const fileRef = ref(storage, `tm-events/${userId}_${Date.now()}_${bannerFile.name}`);
      await uploadBytes(fileRef, bannerFile);
      bannerUrl = await getDownloadURL(fileRef);
    }

    const data = {
      ...eventData,
      bannerUrl,
      createdBy: userId,
      attendees: [], // array of userIds
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const newEventRef = await addDoc(eventsRef, data);
    const newEventDoc = await getDoc(newEventRef);

    return { id: newEventDoc.id, ...(newEventDoc.data() || {}) };
  } catch (error) {
    console.error('createEvent error', error);
    throw error;
  }
};

/** Join an event (adds userId to attendees) */
export const joinEvent = async (eventId, userId) => {
  try {
    const eventRef = doc(db, 'tm-events', eventId);
    await updateDoc(eventRef, {
      attendees: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
    const eventSnap = await getDoc(eventRef);
    const eventData = eventSnap.exists() ? eventSnap.data() : null;
    if (eventData?.createdBy && eventData.createdBy !== userId) {
      await createNotification({
        recipientId: eventData.createdBy,
        actorId: userId,
        type: 'event',
        subtype: 'join',
        title: 'New event attendee',
        message: 'Someone joined your event.',
        entityType: 'event',
        entityId: eventId,
        link: `/events/${eventId}`
      });
    }
    return true;
  } catch (error) {
    console.error('joinEvent error', error);
    throw error;
  }
};

/** Leave an event (removes userId from attendees) */
export const leaveEvent = async (eventId, userId) => {
  try {
    const eventRef = doc(db, 'tm-events', eventId);
    await updateDoc(eventRef, {
      attendees: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('leaveEvent error', error);
    throw error;
  }
};

/** Fetch upcoming events (public or for this institution / network as required) */
export const fetchEvents = async ({ onlyPublic = true, limitResults = 50 } = {}) => {
  try {
    const eventsRef = collection(db, 'tm-events');
    // Fetch events ordered by date (assume date stored as ISO-string or timestamp)
    const q = query(eventsRef, orderBy('date', 'asc'));
    const snap = await getDocs(q);
    const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return events.slice(0, limitResults);
  } catch (error) {
    console.error('fetchEvents error', error);
    throw error;
  }
};

/** Subscribe to events for real-time updates */
export const subscribeToEvents = (callback) => {
  const eventsRef = collection(db, 'tm-events');
  const q = query(eventsRef, orderBy('date', 'asc'));
  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(events);
  }, (err) => {
    console.error('subscribeToEvents error', err);
  });
};

/**
 * Repost an event to feed: creates a post that references the eventId.
 * reUses your existing createPost function (which should exist in this service).
 * postContentOverrides is optional to customise the post text.
 */
export const repostEventToFeed = async (userId, event) => {
  try {
    // Build post payload consistent with your post structure
    const postPayload = {
      content: `📅 ${event.title}\n\n${event.description || ''}\n\nWhen: ${event.date}\nWhere: ${event.location || 'TBD'}`,
      mediaUrl: event.bannerUrl || null,
      mediaType: event.bannerUrl ? 'image' : null,
      eventId: event.id,
      authorId: userId,
      isPublic: true,
      createdAt: serverTimestamp()
    };

    // If your file contains a createPost function locally:
    if (typeof createPost === 'function') {
      // createPost should add a document to 'posts' collection and return it
      const result = await createPost(userId, postPayload);
      return result;
    } else {
      // Fallback: create post directly
      const postsRef = collection(db, 'posts');
      const newPostRef = await addDoc(postsRef, {
        ...postPayload,
        createdAt: serverTimestamp()
      });
      const postDoc = await getDoc(newPostRef);
      return { id: postDoc.id, ...(postDoc.data() || {}) };
    }
  } catch (error) {
    console.error('repostEventToFeed error', error);
    throw error;
  }
};

// Groups
const normalizeValue = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
};

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const doesUserMatchGroupFilters = (profile, filters = {}) => {
  const subjects = filters.subjects || [];
  const grades = filters.grades || [];
  const schools = filters.schools || [];
  const districts = filters.districts || [];
  const projects = filters.projects || [];

  const userSubjects = toArray(profile?.subjects).map(normalizeValue).filter(Boolean);
  const userGrades = toArray(profile?.grades).map(normalizeValue).filter(Boolean);
  const userSchool = normalizeValue(profile?.school);
  const userDistrict = normalizeValue(profile?.district);
  const userProjects = toArray(profile?.projects).map(normalizeValue).filter(Boolean);

  const hasIntersection = (userValues, criteria) => {
    if (!criteria || criteria.length === 0) return true;
    if (!userValues || userValues.length === 0) return false;
    const normalized = criteria.map(normalizeValue).filter(Boolean);
    return normalized.some((value) => userValues.includes(value));
  };

  if (!hasIntersection(userSubjects, subjects)) return false;
  if (!hasIntersection(userGrades, grades)) return false;

  if (schools.length > 0) {
    if (!userSchool) return false;
    const normalizedSchools = schools.map(normalizeValue).filter(Boolean);
    if (!normalizedSchools.includes(userSchool)) return false;
  }

  if (districts.length > 0) {
    if (!userDistrict) return false;
    const normalizedDistricts = districts.map(normalizeValue).filter(Boolean);
    if (!normalizedDistricts.includes(userDistrict)) return false;
  }

  if (!hasIntersection(userProjects, projects)) return false;

  return true;
};

const getGroupRef = (groupId) => doc(db, "tm-groups", groupId);

export const createGroup = async (userId, groupData) => {
  if (!userId) throw new Error("User ID is required to create a group");
  if (!groupData?.name) throw new Error("Group name is required");

  const joinCode = uuidv4();
  const groupRef = await addDoc(collection(db, "tm-groups"), {
    name: groupData.name.trim(),
    purpose: groupData.purpose?.trim() || '',
    createdBy: userId,
    visibility: groupData.visibility || 'private',
    joinPolicy: groupData.joinPolicy || 'invite',
    allowLinkJoin: Boolean(groupData.allowLinkJoin),
    joinCode,
    filters: {
      subjects: groupData.filters?.subjects || [],
      grades: groupData.filters?.grades || [],
      schools: groupData.filters?.schools || [],
      districts: groupData.filters?.districts || [],
      projects: groupData.filters?.projects || []
    },
    memberCount: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  await setDoc(doc(groupRef, "tm-group-members", userId), {
    userId,
    role: 'owner',
    joinedAt: serverTimestamp(),
    addedBy: userId
  });

  const groupSnap = await getDoc(groupRef);
  return { id: groupSnap.id, ...(groupSnap.data() || {}) };
};

export const fetchGroupById = async (groupId) => {
  const groupSnap = await getDoc(getGroupRef(groupId));
  if (!groupSnap.exists()) return null;
  return { id: groupSnap.id, ...groupSnap.data() };
};

export const fetchUserGroups = async (userId) => {
  if (!userId) return [];
  const membersRef = collectionGroup(db, "tm-group-members");
  const q = query(membersRef, where("userId", "==", userId));
  let snapshot;
  try {
    snapshot = await getDocs(q);
  } catch (error) {
    console.error("fetchUserGroups: failed to read tm-group-members collectionGroup", {
      userId,
      error
    });
    throw error;
  }
  const groups = [];

  for (const docSnap of snapshot.docs) {
    const membership = docSnap.data();
    const groupId = docSnap.ref.parent?.parent?.id;
    if (!groupId) continue;
    let groupSnap;
    try {
      groupSnap = await getDoc(getGroupRef(groupId));
    } catch (error) {
      console.error("fetchUserGroups: failed to read group doc", {
        userId,
        groupId,
        membershipDocId: docSnap.id,
        error
      });
      throw error;
    }
    if (!groupSnap.exists()) continue;
    groups.push({
      id: groupSnap.id,
      ...groupSnap.data(),
      membership
    });
  }

  return groups;
};

export const fetchPublicGroups = async (limitCount = 50) => {
  const groupsRef = collection(db, "tm-groups");
  const q = query(groupsRef, where("visibility", "==", "public"), limit(limitCount));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
};

const addMemberToGroup = async (groupId, userId, addedBy = null) => {
  await setDoc(doc(getGroupRef(groupId), "tm-group-members", userId), {
    userId,
    role: 'member',
    joinedAt: serverTimestamp(),
    addedBy: addedBy || userId
  }, { merge: true });
  await updateDoc(getGroupRef(groupId), {
    memberCount: increment(1),
    updatedAt: serverTimestamp()
  });
};

const ensureUserEligibleForGroup = async (group, userId) => {
  if (!group?.filters) return true;
  const profile = await fetchUserProfile(userId);
  if (!profile) return false;
  return doesUserMatchGroupFilters(profile, group.filters);
};

export const requestToJoinGroup = async (groupId, userId, message = '') => {
  if (!groupId || !userId) throw new Error("Group ID and user ID are required");

  const group = await fetchGroupById(groupId);
  if (!group) throw new Error("Group not found");

  if (group.joinPolicy !== 'request' && group.visibility !== 'public') {
    throw new Error("This group does not accept join requests");
  }

  const eligible = await ensureUserEligibleForGroup(group, userId);
  if (!eligible) throw new Error("You do not meet the group criteria to join");

  const memberSnap = await getDoc(doc(getGroupRef(groupId), "tm-group-members", userId));
  if (memberSnap.exists()) return { status: 'already_member' };

  const requestRef = doc(getGroupRef(groupId), "tm-group-join-requests", userId);
  const existing = await getDoc(requestRef);
  if (existing.exists() && existing.data()?.status === 'pending') {
    return { status: 'pending' };
  }

  await setDoc(requestRef, {
    userId,
    groupId,
    status: 'pending',
    message: message || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  if (group.createdBy && group.createdBy !== userId) {
    await createNotification({
      recipientId: group.createdBy,
      actorId: userId,
      type: 'group',
      subtype: 'join_request',
      title: 'New group request',
      message: 'Someone requested to join your group.',
      entityType: 'group',
      entityId: groupId,
      link: `/groups/${groupId}`
    });
  }

  return { status: 'requested' };
};

export const fetchGroupJoinRequests = async (groupId) => {
  const requestsRef = collection(getGroupRef(groupId), "tm-group-join-requests");
  const q = query(requestsRef, where("status", "==", "pending"));
  const snapshot = await getDocs(q);
  const requests = [];
  for (const docSnap of snapshot.docs) {
    const request = { id: docSnap.id, ...docSnap.data() };
    const userDoc = await getDoc(doc(db, "userProfiles", request.userId));
    requests.push({
      ...request,
      user: userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : { id: request.userId, name: "Unknown User" }
    });
  }
  return requests;
};

export const respondToJoinRequest = async (groupId, userId, approve = true, adminId) => {
  const requestRef = doc(getGroupRef(groupId), "tm-group-join-requests", userId);
  const requestSnap = await getDoc(requestRef);
  if (!requestSnap.exists()) throw new Error("Join request not found");

  await updateDoc(requestRef, {
    status: approve ? 'approved' : 'rejected',
    reviewedBy: adminId || null,
    updatedAt: serverTimestamp()
  });

  if (approve) {
    await addMemberToGroup(groupId, userId, adminId);
  }
  await createNotification({
    recipientId: userId,
    actorId: adminId || null,
    type: 'group',
    subtype: approve ? 'join_approved' : 'join_rejected',
    title: approve ? 'Group request approved' : 'Group request declined',
    message: approve
      ? 'Your request to join the group was approved.'
      : 'Your request to join the group was declined.',
    entityType: 'group',
    entityId: groupId,
    link: `/groups/${groupId}`
  });
  return { success: true };
};

export const joinGroupViaLink = async (groupId, userId, joinCode) => {
  if (!groupId || !userId) throw new Error("Group ID and user ID are required");

  const group = await fetchGroupById(groupId);
  if (!group) throw new Error("Group not found");

  if (!group.allowLinkJoin || group.joinPolicy !== 'open') {
    throw new Error("This group does not allow link-based joining");
  }

  if (group.joinCode) {
    if (!joinCode || group.joinCode !== joinCode) {
      throw new Error("Invalid join link");
    }
  }

  const eligible = await ensureUserEligibleForGroup(group, userId);
  if (!eligible) throw new Error("You do not meet the group criteria to join");

  const memberSnap = await getDoc(doc(getGroupRef(groupId), "tm-group-members", userId));
  if (memberSnap.exists()) return { status: 'already_member' };

  await addMemberToGroup(groupId, userId, userId);
  return { status: 'joined' };
};

export const sendGroupInvite = async (groupId, fromUserId, toUserId, message = '') => {
  if (!groupId || !fromUserId || !toUserId) throw new Error("Missing invite parameters");
  if (fromUserId === toUserId) throw new Error("Cannot invite yourself");

  const group = await fetchGroupById(groupId);
  if (!group) throw new Error("Group not found");

  const memberSnap = await getDoc(doc(getGroupRef(groupId), "tm-group-members", toUserId));
  if (memberSnap.exists()) return { status: 'already_member' };

  const invitesRef = collection(getGroupRef(groupId), "tm-group-invites");
  const existingQuery = query(
    invitesRef,
    where("toUserId", "==", toUserId),
    where("status", "==", "pending")
  );
  const existingSnap = await getDocs(existingQuery);
  if (!existingSnap.empty) return { status: 'already_invited' };

  const inviteRef = await addDoc(invitesRef, {
    groupId,
    groupName: group.name || '',
    groupPurpose: group.purpose || '',
    fromUserId,
    toUserId,
    message: message?.trim() || '',
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  await createNotification({
    recipientId: toUserId,
    actorId: fromUserId,
    type: 'group',
    subtype: 'invite',
    title: 'Group invite',
    message: 'You have been invited to join a group.',
    entityType: 'group',
    entityId: groupId,
    link: `/groups/${groupId}`
  });

  return { id: inviteRef.id, status: 'invited' };
};

export const fetchGroupInvitesForUser = async (userId) => {
  if (!userId) return [];
  const invitesRef = collectionGroup(db, "tm-group-invites");
  const q = query(invitesRef, where("toUserId", "==", userId), where("status", "==", "pending"));
  const snapshot = await getDocs(q);
  const invites = [];
  for (const docSnap of snapshot.docs) {
    const invite = { id: docSnap.id, ...docSnap.data() };
    const groupId = invite.groupId || docSnap.ref.parent?.parent?.id;
    const groupSnap = groupId ? await getDoc(getGroupRef(groupId)) : null;
    const group = groupSnap && groupSnap.exists() ? { id: groupSnap.id, ...groupSnap.data() } : null;
    invites.push({ ...invite, group });
  }
  return invites;
};

export const respondToGroupInvite = async (groupId, inviteId, userId, accept = true) => {
  if (!groupId || !inviteId || !userId) throw new Error("Missing invite response parameters");
  const inviteRef = doc(getGroupRef(groupId), "tm-group-invites", inviteId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) throw new Error("Invite not found");

  await updateDoc(inviteRef, {
    status: accept ? 'accepted' : 'declined',
    updatedAt: serverTimestamp()
  });

  if (accept) {
    await addMemberToGroup(groupId, userId, inviteSnap.data()?.fromUserId || null);
  }

  const inviterId = inviteSnap.data()?.fromUserId;
  if (inviterId && inviterId !== userId) {
    await createNotification({
      recipientId: inviterId,
      actorId: userId,
      type: 'group',
      subtype: accept ? 'invite_accepted' : 'invite_declined',
      title: accept ? 'Invite accepted' : 'Invite declined',
      message: accept
        ? 'Your group invite was accepted.'
        : 'Your group invite was declined.',
      entityType: 'group',
      entityId: groupId,
      link: `/groups/${groupId}`
    });
  }

  return { success: true };
};

export const fetchGroupMembership = async (groupId, userId) => {
  if (!groupId || !userId) return null;
  const memberSnap = await getDoc(doc(getGroupRef(groupId), "tm-group-members", userId));
  if (!memberSnap.exists()) return null;
  return { id: memberSnap.id, ...memberSnap.data() };
};

export const fetchGroupMembers = async (groupId) => {
  if (!groupId) return [];
  const membersRef = collection(getGroupRef(groupId), "tm-group-members");
  const snapshot = await getDocs(membersRef);
  const members = [];

  for (const docSnap of snapshot.docs) {
    const member = { id: docSnap.id, ...docSnap.data() };
    const userDoc = await getDoc(doc(db, "userProfiles", member.userId));
    members.push({
      ...member,
      user: userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : { id: member.userId, name: "Unknown User" }
    });
  }

  return members;
};
